/**
 * The panel down the right: what is selected, and everything worth changing
 * about it.
 *
 * A tile carries more than one thing. A wall on each side, something in the
 * middle, a character standing on it, and any number of spawn zones covering
 * it. Rather than guessing which one was meant, the panel puts a tab up for
 * each and lets the choice be made after the click.
 */

import type {
  DropTablePacket,
  ItemDefinitionWire,
  NpcDefinitionWire,
  GatheringPacket,
  QuestWire,
  ShopStockPacket,
  ZoneWire,
} from '@inverse/protocol';
import { useMemo, useState } from 'react';
import { MODEL_NAMES } from '../../game/avatars';
import type { Place } from './places-list';
import { ColourChoice } from '../Colours';
import { colourText, colourValue } from '../colour-values';
import type { AdminCommand } from '../Admin';
import { ItemPicker } from './ItemPicker';
import { Slider } from './Palette';
import {
  activePick,
  objectPicks,
  secondsOf,
  tabNameOf,
  type EditorMode,
  type Facts,
  type Pick,
  type Selection,
  type TileArea,
  type TransformTool,
} from './model';
import { DropEditor, type DropTest } from './Drops';
import { ActsEditor } from './Acts';
import type { Act } from './objects';
import { ShapeBuilder, type ShapePiece } from './Shape';
import { ShopEditor } from './Shop';
import { Explain } from '../Explain';
import { Conditions, type Condition } from './Dialogue';

export function EditorDetails({
  waysAt,
  selection,
  onActive,
  pinned,
  onPin,
  showingPinned,
  mode,
  tool,
  facts,
  drops,
  dropTest,
  shop,
  gathering,
  items,
  onTalk,
  characters,
  quests,
  zones,
  describeObject,
  tiersOf,
  onSwapTier,
  shapes,
  places,
  groundAt,
  cornersAt,
  areasNow,
  areaAt,
  describeTile,
  onCommand,
  spotActs,
  shops,
  onTransform,
  onReplace,
  onDuplicate,
  onVary,
  onKeepPiece,
  replacement,
  onClear,
  brush,
  lastArea,
  drawingRoam,
  onDrawRoam,
  zonePaint,
  onZonePaint,
  onTool,
}: {
  /**
   * What one particular thing offers, read from the world.
   *
   * Not from what was picked: turning a way on or off arrives as a patch, and a
   * panel drawn from a snapshot would go on showing what it offered when it was
   * chosen.
   */
  waysAt: (tx: number, tz: number, objectId: number) => { action: string }[] | undefined;
  selection: Selection;
  onActive: (index: number) => void;
  /** One thing kept here whatever else is selected, and how to keep it. */
  pinned: Pick | null;
  onPin: (pick: Pick | null) => void;
  /** Whether the pinned thing is what the pane is showing. */
  showingPinned: boolean;
  mode: EditorMode;
  tool: TransformTool;
  facts: Facts | null;
  drops: DropTablePacket | null;
  dropTest: DropTest | null;
  shop: ShopStockPacket | null;
  gathering: readonly GatheringPacket[];
  items: readonly ItemDefinitionWire[];
  /** Opens this character's conversation on the canvas. */
  onTalk: (npcId: number) => void;
  characters: readonly NpcDefinitionWire[];
  /** Every quest there is, for conditions that name one. */
  quests: readonly QuestWire[];
  zones: readonly ZoneWire[];
  /** The other tiers of one kind of scenery, and how to change which one it is. */
  tiersOf: (objectId: number) => { id: number; name: string; tier?: string }[];
  onSwapTier: (objectId: number) => void;
  /** Everything there is, for choosing what one particular thing is drawn as. */
  shapes: readonly { id: number; name: string }[];
  describeObject: (id: number) =>
    | {
        name: string;
        examine: string;
        actions: string[];
        parts?: string;
        dungeon?: string;
        exit?: boolean;
      }
    | undefined;
  /** The regions there are, for a door that leads into one. */
  places: readonly Place[];
  /** What a tile is made of, and what walking across it is like. */
  groundAt: (tx: number, tz: number) => number;
  /** The four corner heights of a tile, in the order the pane lists them. */
  cornersAt: (tx: number, tz: number) => readonly number[];
  /**
   * The named places in this world, in the order the ground refers to them.
   *
   * Asked for rather than handed over, because the only thing that knows them
   * is the running game, and reading that during a render is reading a ref
   * during a render.
   */
  areasNow: () => readonly { name: string; town?: boolean }[];
  /** Which of them a tile belongs to, one past the start, or nought for none. */
  areaAt: (tx: number, tz: number) => number;
  describeTile: (tx: number, tz: number) => string;
  onCommand: AdminCommand;
  /** What the thing being looked at does, once it has been asked for. */
  spotActs: {
    tx: number;
    tz: number;
    plane: number;
    own: boolean;
    acts: string;
  } | null;
  shops: readonly { id: number; name: string }[];
  onTransform: (
    action: 'move' | 'turn' | 'scale' | 'remove',
    value: number,
    dx?: number,
    dz?: number,
  ) => void;
  /** Swaps everything held for whatever the palette has chosen. */
  onReplace: () => void;
  onDuplicate: () => void;
  onVary: () => void;
  /** Keeps everything held as a piece that can be built again. */
  onKeepPiece: (name: string) => void;
  /** What the palette has chosen, which is what a swap would put down. */
  replacement: string | null;
  onClear: () => void;
  brush: number;
  /** The last area drawn on the ground, which a character can be told to keep to. */
  lastArea: TileArea | null;
  /** Whether the next box dragged belongs to the held character's roam area. */
  drawingRoam: boolean;
  onDrawRoam: (on: boolean) => void;
  zonePaint: 'add' | 'remove';
  onZonePaint: (mode: 'add' | 'remove') => void;
  onTool: (tool: TransformTool) => void;
}): JSX.Element {
  // The pinned thing is not one of the picks: it has a tab of its own, and it
  // keeps the pane until another tab is chosen. Staying put is the whole point.
  const pick = showingPinned && pinned ? pinned : activePick(selection);
  const held = objectPicks(selection);

  const objectName = (id: number): string => describeObject(id)?.name ?? `Scenery ${id}`;
  const characterName = (entityId: number): string => facts?.text.name ?? `Character ${entityId}`;
  const zoneName = (zoneId: number): string => {
    const zone = zones.find((entry) => entry.id === zoneId);
    const npc = characters.find((entry) => entry.id === zone?.npcId);
    return zone ? `${npc?.name ?? 'Zone'} zone` : 'Zone';
  };

  return (
    <>
      {selection.picks.length > 0 ? (
        <button type="button" className="editor-tool wide" onClick={onClear}>
          Clear Selection
        </button>
      ) : null}

      {selection.picks.length > 1 || pinned ? (
        <div className="editor-tabs">
          {pinned ? (
            <button
              type="button"
              className={showingPinned ? 'editor-tab on pinned' : 'editor-tab pinned'}
              title="Pinned. Click to unpin"
              onClick={() => onActive(-1)}
            >
              📌 {tabNameOf(pinned, objectName, characterName, zoneName)}
            </button>
          ) : null}

          {selection.picks.map((entry, index) => (
            <button
              key={`${entry.kind}-${index}`}
              type="button"
              className={
                index === selection.active && !showingPinned ? 'editor-tab on' : 'editor-tab'
              }
              onClick={() => onActive(index)}
            >
              {tabNameOf(entry, objectName, characterName, zoneName)}
            </button>
          ))}
        </div>
      ) : null}

      {pick ? (
        <button
          type="button"
          className="editor-tool wide"
          onClick={() => onPin(showingPinned ? null : pick)}
        >
          {showingPinned ? 'Unpin' : 'Pin Selection'}
        </button>
      ) : null}

      <div className="editor-body">
        {!pick ? (
          <p className="hint">
            Nothing selected. Click something in the world, or drag out an area over it.
          </p>
        ) : null}

        {held.length > 1 ? (
          <>
            <h3>Selection</h3>
            <div className="editor-fact">
              <span>Objects</span>
              <span>{held.length}</span>
            </div>

            {/* What is actually held, by kind, since a count on its own does not
                say whether the whole building came up or only its floors. */}
            {[...countByKind(held, describeObject)].map(([what, many]) => (
              <div className="editor-fact" key={what}>
                <span>{what}</span>
                <span>{many}</span>
              </div>
            ))}

            <p className="hint">Everything held moves, turns and resizes together.</p>
          </>
        ) : null}

        {held.length > 0 ? (
          <Transforms
            tool={tool}
            onTransform={onTransform}
            count={held.length}
            replacement={replacement}
            onReplace={onReplace}
            onDuplicate={onDuplicate}
            onVary={onVary}
            onKeepPiece={onKeepPiece}
          />
        ) : null}

        {pick?.kind === 'object' ? (
          <ObjectDetails
            waysAt={waysAt}
            pick={pick}
            describeObject={describeObject}
            family={tiersOf(pick.objectId)}
            onSwapTier={onSwapTier}
            shapes={shapes}
            places={places}
            gathering={gathering}
            items={items}
            onCommand={onCommand}
            onTransform={onTransform}
            spotActs={spotActs}
            shops={shops}
            quests={quests}
            characters={characters}
          />
        ) : null}

        {pick?.kind === 'character' ? (
          <CharacterDetails
            entityId={pick.entityId}
            at={
              pick.tx !== undefined && pick.tz !== undefined ? { tx: pick.tx, tz: pick.tz } : null
            }
            facts={facts}
            onTalk={onTalk}
            drops={drops}
            dropTest={dropTest}
            shop={shop}
            items={items}
            quests={quests}
            characters={characters}
            shapes={shapes}
            lastArea={lastArea}
            drawingRoam={drawingRoam}
            onDrawRoam={onDrawRoam}
            painting={zonePaint}
            onPainting={onZonePaint}
            onTool={onTool}
            onCommand={onCommand}
            onClear={onClear}
          />
        ) : null}

        {pick?.kind === 'zone' ? (
          <ZoneDetails
            zone={zones.find((entry) => entry.id === pick.zoneId)}
            characters={characters}
            brush={brush}
            painting={zonePaint}
            onPainting={onZonePaint}
            onTool={onTool}
            onCommand={onCommand}
            onClear={onClear}
          />
        ) : null}

        {pick?.kind === 'tile' ? (
          <>
            <h3>Ground</h3>
            <div className="editor-fact">
              <span>Position</span>
              <span>
                {pick.tx}, {pick.tz}
              </span>
            </div>
            <div className="editor-fact">
              <span>Material</span>
              <span>{groundName(groundAt(pick.tx, pick.tz))}</span>
            </div>
            <div className="editor-fact">
              <span>Movement</span>
              <span>{describeTile(pick.tx, pick.tz)}</span>
            </div>
            <p className="hint">
              {mode === 'terrain'
                ? 'Pick what to paint with, then click for the brush or drag for an area.'
                : 'Nothing is standing here.'}
            </p>

            <PlaceDetails
              areas={areasNow()}
              here={areaAt(pick.tx, pick.tz)}
              lastArea={lastArea}
              onCommand={onCommand}
            />

            <h4>Corners</h4>
            <div className="editor-corners">
              {(
                [
                  [0, 'North west'],
                  [1, 'North east'],
                  [2, 'South west'],
                  [3, 'South east'],
                ] as const
              ).map(([corner, label]) => (
                <div className="editor-corner" key={corner}>
                  <span>{label}</span>
                  <strong>{(cornersAt(pick.tx, pick.tz)[corner] ?? 0).toFixed(2)}</strong>
                  <div className="editor-brushes">
                    <button
                      type="button"
                      className="editor-tab"
                      onClick={() =>
                        onCommand('setCorner', { tx: pick.tx, tz: pick.tz, corner, by: 0.25 })
                      }
                    >
                      Up
                    </button>
                    <button
                      type="button"
                      className="editor-tab"
                      onClick={() =>
                        onCommand('setCorner', { tx: pick.tx, tz: pick.tz, corner, by: -0.25 })
                      }
                    >
                      Down
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <p className="hint">
              One corner at a time.
              <Explain>
                How a ramp or the lip of a cliff is made, rather than approached with a brush.
              </Explain>
            </p>

            <h4>Movement</h4>
            <div className="editor-brushes">
              {(
                [
                  ['clear', 'Open'],
                  ['blocked', 'Blocked'],
                  ['water', 'Shallow water'],
                  ['lake', 'Deep water'],
                ] as const
              ).map(([flags, label]) => (
                <button
                  key={flags}
                  type="button"
                  className="editor-tab"
                  onClick={() => onCommand('setTile', { tx: pick.tx, tz: pick.tz }, { flags })}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="hint">
              One tile at a time.
              <Explain>
                For the corner a brush cannot reach without taking its neighbours with it.
              </Explain>
            </p>
          </>
        ) : null}

        {pick?.kind === 'tiles' ? (
          <>
            <h3>Ground</h3>
            <div className="editor-fact">
              <span>Tiles</span>
              <span>{pick.tiles.length}</span>
            </div>
            <p className="hint">
              Drawn rather than boxed.
              <Explain>
                The Landscape panel works on all of it at once, and one press takes the whole stroke
                back.
              </Explain>
            </p>
          </>
        ) : null}

        {pick?.kind === 'area' ? (
          <>
            <h3>Area</h3>
            <div className="editor-fact">
              <span>Origin</span>
              <span>
                {pick.area.tx}, {pick.area.tz}
              </span>
            </div>
            <div className="editor-fact">
              <span>Size</span>
              <span>
                {pick.area.width} by {pick.area.height}
              </span>
            </div>
            <div className="editor-fact">
              <span>Tiles</span>
              <span>{pick.area.width * pick.area.height}</span>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
}

/**
 * Moving, turning and resizing whatever is held.
 *
 * The buttons are here as well as on the pointer because a nudge of exactly one
 * tile is worth being able to ask for, and because a drag cannot say "a quarter
 * turn" without ambiguity.
 */
function Transforms({
  tool,
  onTransform,
  count,
  replacement,
  onReplace,
  onDuplicate,
  onVary,
  onKeepPiece,
}: {
  tool: TransformTool;
  onTransform: (
    action: 'move' | 'turn' | 'scale' | 'remove',
    value: number,
    dx?: number,
    dz?: number,
  ) => void;
  count: number;
  replacement: string | null;
  onReplace: () => void;
  onDuplicate: () => void;
  onVary: () => void;
  onKeepPiece: (name: string) => void;
}): JSX.Element {
  const [pieceName, setPieceName] = useState('');
  return (
    <>
      <h3>Transform</h3>

      <div className="editor-nudge">
        <button type="button" onClick={() => onTransform('move', 0, 0, -1)}>
          North
        </button>
        <button type="button" onClick={() => onTransform('move', 0, -1, 0)}>
          West
        </button>
        <button type="button" onClick={() => onTransform('move', 0, 1, 0)}>
          East
        </button>
        <button type="button" onClick={() => onTransform('move', 0, 0, 1)}>
          South
        </button>
      </div>

      {/* Turning and resizing, as glyphs: the words were three times the size
          of the thing they described. What each one does is in its tooltip. */}
      <div className="editor-icons">
        <button
          type="button"
          className="editor-tab"
          title="Turn it right"
          onClick={() => onTransform('turn', 1)}
        >
          ↻
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Turn it left"
          onClick={() => onTransform('turn', 3)}
        >
          ↺
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Make it larger"
          onClick={() => onTransform('scale', 20)}
        >
          +
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Make it smaller"
          onClick={() => onTransform('scale', 13)}
        >
          −
        </button>
        <button type="button" className="editor-tab" title="Duplicate" onClick={onDuplicate}>
          Dup
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Randomise size and rotation"
          onClick={onVary}
        >
          Vary
        </button>
        {replacement ? (
          <button
            type="button"
            className="editor-tab"
            title={`Swap for ${replacement}`}
            onClick={onReplace}
          >
            Swap
          </button>
        ) : null}
      </div>

      {count > 1 ? (
        <div className="editor-drop-add">
          <input
            className="editor-search"
            type="text"
            value={pieceName}
            placeholder="Keep these as a piece"
            onChange={(event) => setPieceName(event.target.value)}
          />
          <button
            type="button"
            className="wide"
            disabled={pieceName.trim().length === 0}
            onClick={() => {
              onKeepPiece(pieceName);
              setPieceName('');
            }}
          >
            Keep them
          </button>
        </div>
      ) : null}

      <p className="hint">
        {tool === 'move'
          ? `Drag the world to move ${count === 1 ? 'it' : 'them'}.`
          : tool === 'rotate'
            ? 'Drag left or right to turn.'
            : tool === 'scale'
              ? 'Drag up or down to resize.'
              : 'Choose Move, Rotate or Scale to work with the pointer.'}
      </p>
    </>
  );
}

function ObjectDetails({
  waysAt,
  pick,
  describeObject,
  family,
  onSwapTier,
  shapes,
  places,
  gathering,
  items,
  onCommand,
  onTransform,
  spotActs,
  shops,
  quests,
  characters,
}: {
  /** What one particular thing offers, read from the world rather than the pick. */
  waysAt: (tx: number, tz: number, objectId: number) => { action: string }[] | undefined;
  pick: {
    tx: number;
    tz: number;
    objectId: number;
    rotation: number;
    scale?: number;
    /** A shape of its own, borrowed from something else. */
    looks?: number;
    /** What this one offers, when it has been told rather than left to its kind. */
    ways?: { action: string }[];
  };
  describeObject: (id: number) =>
    | {
        name: string;
        examine: string;
        actions: string[];
        parts?: string;
        dungeon?: string;
        exit?: boolean;
      }
    | undefined;
  /** The other tiers of whatever this is one of, itself included. */
  family: readonly { id: number; name: string; tier?: string }[];
  onSwapTier: (objectId: number) => void;
  /** Everything there is, for choosing what this one is drawn as. */
  shapes: readonly { id: number; name: string }[];
  places: readonly Place[];
  gathering: readonly GatheringPacket[];
  items: readonly ItemDefinitionWire[];
  onCommand: AdminCommand;
  onTransform: (action: 'move' | 'turn' | 'scale' | 'remove', value: number) => void;
  spotActs: {
    tx: number;
    tz: number;
    plane: number;
    own: boolean;
    acts: string;
  } | null;
  shops: readonly { id: number; name: string }[];
  quests: readonly QuestWire[];
  characters: readonly { id: number; name: string }[];
}): JSX.Element {
  const definition = describeObject(pick.objectId);
  // Quarter turns, anticlockwise from south, which is the order every wall in
  // the world was built with. See nearestSide for why it starts where it does.
  const sides = ['south', 'east', 'north', 'west'];

  return (
    <>
      <h3>{definition?.name ?? `Scenery ${pick.objectId}`}</h3>

      {/* One of a family: which one it is can be changed here, and everything
          about the tier travels with the swap because each tier is its own
          kind of scenery. */}
      {family.length > 1 ? (
        <div className="editor-brushes">
          {family.map((entry) => (
            <button
              key={entry.id}
              type="button"
              className={entry.id === pick.objectId ? 'editor-tab on' : 'editor-tab'}
              title={`Make it a ${entry.name}`}
              onClick={() => onSwapTier(entry.id)}
            >
              {entry.tier || entry.name}
            </button>
          ))}
        </div>
      ) : null}

      {/* What this one is drawn as, which is not always what its kind is drawn
          as. A wood of pines and oaks is one kind of tree wearing two shapes:
          what it is for belongs to the kind, and what it looks like belongs to
          the one standing here. */}
      <label className="editor-line">
        <span>Drawn as</span>
        <select
          value={pick.looks ?? 0}
          onChange={(event) =>
            onCommand(
              'setSpotLooks',
              { tx: pick.tx, tz: pick.tz, looks: Number(event.target.value) },
              {},
            )
          }
        >
          <option value={0}>its own kind</option>
          {shapes.map((one) => (
            <option key={one.id} value={one.id}>
              {one.name}
            </option>
          ))}
        </select>
      </label>

      <div className="editor-fact">
        <span>Position</span>
        <span>
          {pick.tx}, {pick.tz}
        </span>
      </div>
      <div className="editor-fact">
        <span>Rotation</span>
        <span>{sides[pick.rotation & 3]}</span>
      </div>
      <div className="editor-fact">
        <span>Actions</span>
        <span>{definition?.actions.join(', ') || 'nothing'}</span>
      </div>
      <div className="editor-fact">
        <span>Scale</span>
        <span>{Math.round((pick.scale ?? 1) * 100)}%</span>
      </div>
      {definition?.examine ? <p className="hint">{definition.examine}</p> : null}

      {(pick.scale ?? 1) !== 1 ? (
        <button type="button" className="wide" onClick={() => onTransform('scale', 16)}>
          Back to its usual size
        </button>
      ) : null}

      <SceneryEditor pick={pick} definition={definition} places={places} onCommand={onCommand} />

      <WaysToWork
        at={{ tx: pick.tx, tz: pick.tz }}
        offers={waysAt(pick.tx, pick.tz, pick.objectId) ?? pick.ways}
        actions={definition?.actions ?? []}
        gathering={gathering.filter((entry) => entry.objectId === pick.objectId)}
        items={items}
        onCommand={onCommand}
      />

      <WhatThisOneDoes
        at={{ tx: pick.tx, tz: pick.tz }}
        name={definition?.name ?? 'it'}
        menu={definition?.actions ?? []}
        held={spotActs}
        items={items}
        quests={quests}
        characters={characters}
        objects={shapes}
        shops={shops}
        onCommand={onCommand}
      />

      <button type="button" className="wide danger" onClick={() => onTransform('remove', 0)}>
        Remove it
      </button>
    </>
  );
}

/**
 * What one particular thing does when it is pressed.
 *
 * The kind says which menu entries it offers; this says what one of them does
 * on this one thing. That division is the whole of why it is worth having: a
 * Lever offers Pull wherever it stands, and the lever at the end of this
 * corridor opens the gate at the end of this corridor. Written on the kind
 * instead, every lever in the world opens the same gate and a second puzzle
 * wants a second kind of lever.
 *
 * Asked for rather than sent with the ground, because almost nothing standing
 * in the world has any of this and a region is sixty thousand things.
 */
function WhatThisOneDoes({
  at,
  name,
  menu,
  held,
  items,
  quests,
  characters,
  objects,
  shops,
  onCommand,
}: {
  at: { tx: number; tz: number };
  name: string;
  /** What its kind offers, which is what an act here can hang off. */
  menu: readonly string[];
  held: {
    tx: number;
    tz: number;
    plane: number;
    own: boolean;
    acts: string;
  } | null;
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  characters: readonly { id: number; name: string }[];
  objects: readonly { id: number; name: string }[];
  shops: readonly { id: number; name: string }[];
  onCommand: AdminCommand;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);

  // Whatever came back is only this one's if it is about this one: the panel
  // redraws the moment something else is picked, and the answer to the last
  // question arrives after that.
  const mine = held && held.tx === at.tx && held.tz === at.tz ? held : null;

  const acts = useMemo(() => {
    if (!mine) return [];
    try {
      return JSON.parse(mine.acts) as Act[];
    } catch {
      return [];
    }
  }, [mine]);

  const send = (wanted: Act[]): void =>
    onCommand('setSpotActs', { tx: at.tx, tz: at.tz }, { acts: JSON.stringify(wanted) });

  if (menu.length === 0) return null;

  if (!open) {
    return (
      <button
        type="button"
        className="wide"
        onClick={() => {
          setOpen(true);
          onCommand('spotActs', { tx: at.tx, tz: at.tz });
        }}
      >
        What this one does
      </button>
    );
  }

  return (
    <>
      <h4>What this one does</h4>
      <p className="hint">
        {mine?.own
          ? `This ${name.toLowerCase()} has its own.`
          : `Its kind's, shown to start from. Changing anything here is this ${name.toLowerCase()} alone.`}
        <Explain>
          Its kind decides which menu entries it has. This decides what pressing one of them does on
          this one thing, so a lever can be told which gate it opens without every lever in the
          world opening the same one.
        </Explain>
      </p>

      <ActsEditor
        acts={acts}
        menu={menu}
        items={items}
        quests={quests}
        characters={characters}
        objects={objects}
        shops={shops}
        onChange={send}
      />

      {mine?.own ? (
        <button
          type="button"
          className="wide danger"
          onClick={() => {
            send([]);
            onCommand('spotActs', { tx: at.tx, tz: at.tz });
          }}
        >
          Back to whatever its kind does
        </button>
      ) : null}
    </>
  );
}

/**
 * A text field whose changes are sent when they are finished with.
 *
 * Every keystroke used to be a command, and a command that renames a piece of
 * scenery is a full round of definitions to everybody in the world. Held here
 * until the field is left or Enter is pressed, and taken from what the server
 * says whenever nobody is typing in it.
 */
function SettledField({
  label,
  value,
  placeholder,
  limit,
  onCommit,
}: {
  label: string;
  value: string;
  placeholder?: string;
  limit: number;
  onCommit: (value: string) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);

  return (
    <label className="editor-field">
      <span>{label}</span>
      <input
        type="text"
        value={draft ?? value}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value.slice(0, limit))}
        onBlur={() => {
          if (draft !== null && draft !== value) onCommit(draft);
          setDraft(null);
        }}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;

          event.preventDefault();
          if (draft !== null && draft !== value) onCommit(draft);
          setDraft(null);
        }}
      />
    </label>
  );
}

/**
 * What a piece of scenery is called, and what its menu offers.
 *
 * Changed here rather than in a file, and taken by every client at once, so a
 * tree that reads badly can be renamed while standing in front of it. How it
 * blocks is not offered: that is worked into the map when the world loads.
 */
/** What a thing is built out of, as the definitions carry it. */
function shapeOf(definition: { parts?: string } | undefined): ShapePiece[] {
  try {
    const parsed: unknown = JSON.parse(definition?.parts ?? '[]');
    return Array.isArray(parsed) ? (parsed as ShapePiece[]) : [];
  } catch {
    return [];
  }
}

function SceneryEditor({
  pick,
  definition,
  places,
  onCommand,
}: {
  pick: { objectId: number };
  definition:
    | {
        name: string;
        examine: string;
        actions: string[];
        parts?: string;
        dungeon?: string;
        exit?: boolean;
      }
    | undefined;
  places: readonly Place[];
  onCommand: AdminCommand;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  if (!open) {
    return (
      <button type="button" className="wide" onClick={() => setOpen(true)}>
        Change what this is
      </button>
    );
  }

  const change = (text: Record<string, string>): void =>
    onCommand('setObject', { object: pick.objectId }, text);

  return (
    <div className="editor-section">
      <h4>Properties</h4>

      <SettledField
        label="Name"
        value={definition?.name ?? ''}
        limit={48}
        onCommit={(name) => change({ name })}
      />

      <SettledField
        label="Description"
        value={definition?.examine ?? ''}
        placeholder="Shown when examined"
        limit={160}
        onCommit={(examine) => change({ examine })}
      />

      <SettledField
        label="Actions"
        value={definition?.actions.join(', ') ?? ''}
        placeholder="Chop, Search"
        limit={120}
        onCommit={(actions) => change({ actions })}
      />
      <p className="hint">
        Separated by commas, in the order they appear.
        <Explain>
          An action nothing is wired to does nothing, which is what the resource block below is for.
        </Explain>
      </p>

      <DoorChoice
        dungeon={definition?.dungeon ?? ''}
        exit={definition?.exit ?? false}
        places={places}
        onDungeon={(dungeon) => change({ dungeon })}
        onExit={(exit) => change({ exit: exit ? 'yes' : 'no' })}
      />

      <ShapeBuilder objectId={pick.objectId} parts={shapeOf(definition)} onCommand={onCommand} />

      <button type="button" className="wide" onClick={() => onCommand('saveObjects', {})}>
        Save scenery
      </button>
    </div>
  );
}

/**
 * Where a piece of scenery leads.
 *
 * Most scenery leads nowhere. A door does: naming a region here makes going
 * through it make a copy of that region and put whoever went through inside it,
 * and marking something as a way out sends them back to the door they used.
 */
function DoorChoice({
  dungeon,
  exit,
  places,
  onDungeon,
  onExit,
}: {
  dungeon: string;
  exit: boolean;
  places: readonly Place[];
  onDungeon: (region: string) => void;
  onExit: (exit: boolean) => void;
}): JSX.Element {
  return (
    <div className="editor-section">
      <h4>Destination</h4>

      <div className="editor-brushes">
        <button
          type="button"
          className={dungeon === '' && !exit ? 'editor-tab on' : 'editor-tab'}
          onClick={() => {
            onDungeon('');
            onExit(false);
          }}
        >
          Nowhere
        </button>

        {places.map((place) => (
          <button
            key={place.id}
            type="button"
            className={dungeon === place.id ? 'editor-tab on' : 'editor-tab'}
            onClick={() => onDungeon(place.id)}
          >
            {place.name}
          </button>
        ))}

        <button
          type="button"
          className={exit ? 'editor-tab on' : 'editor-tab'}
          onClick={() => onExit(!exit)}
        >
          Back out
        </button>
      </div>

      <p className="hint">
        A door makes its own copy of the place.
        <Explain>What happens in there happens to whoever went through and to nobody else.</Explain>
      </p>
    </div>
  );
}

/**
 * The ways a piece of scenery can be worked, each turned on or off.
 *
 * A fishing spot is not four spots. One stretch of water is netted, another is
 * fished with a rod, and a deep one is harpooned, so which of them this spot
 * offers is a decision somebody makes here rather than a fixed list every spot
 * in the world carries.
 *
 * Turning one on writes both halves: the menu entry the player sees, and the
 * way behind it. Each is folded away until it is opened, because four ways
 * unfolded at once is the panel nobody could read.
 */
const FISHING_WAYS = ['Net', 'Lure', 'Cage', 'Harpoon'] as const;

function WaysToWork({
  at,
  offers,
  actions,
  gathering,
  items,
  onCommand,
}: {
  /** Which one is being looked at, since each one is its own spot. */
  at: { tx: number; tz: number };
  /**
   * What this one offers, when it has been told.
   *
   * Absent means it is worked however its kind is worked, which is where every
   * spot starts.
   */
  offers?: readonly { action: string }[];
  actions: readonly string[];
  gathering: readonly GatheringPacket[];
  items: readonly ItemDefinitionWire[];
  onCommand: AdminCommand;
}): JSX.Element | null {
  const [open, setOpen] = useState<string | null>(null);

  const fishing = gathering.some((entry) => entry.skill === 'fishing');

  /** What its kind knows how to be worked for, whether or not this one offers it. */
  const known = (action: string): GatheringPacket | undefined =>
    gathering.find((entry) => entry.action.toLowerCase() === action.toLowerCase());

  /**
   * Whether this particular one offers it.
   *
   * A spot that has never been told offers whatever its kind does, which is
   * how every spot placed before any of this behaved.
   */
  const has = (action: string): GatheringPacket | undefined => {
    const held = known(action);
    if (!held) return undefined;
    if (!offers) return held;

    return offers.some((way) => way.action.toLowerCase() === action.toLowerCase())
      ? held
      : undefined;
  };

  // The four ways of fishing for anything already fished, and whatever else is
  // written down for everything else. A tree is chopped and that is that.
  const offered = fishing ? [...FISHING_WAYS] : gathering.map((entry) => entry.action);

  if (offered.length === 0) return null;

  return (
    <div className="editor-section">
      <h4>{fishing ? 'Fishing' : 'Gathering'}</h4>

      {fishing ? (
        <p className="hint">
          Which ways this spot can be worked.
          <Explain>
            What a fish takes to catch and what it is worth belong to the fish, under Create, Items.
            Here you say which ways work here and what comes up.
          </Explain>
        </p>
      ) : null}

      {offered.map((action) => {
        const held = has(action);
        const showing = open === action;

        return (
          <div className={held ? 'editor-way on' : 'editor-way'} key={action}>
            <div className="editor-way-head">
              <button
                type="button"
                className={held ? 'editor-tab on' : 'editor-tab'}
                aria-pressed={held !== undefined}
                title={held ? `Stop offering ${action} here` : `Offer ${action} here`}
                onClick={() => {
                  // This one, rather than every one of its kind: a stretch of
                  // water netted and the next one along fished with a rod are
                  // the same scenery.
                  onCommand('setSpotWay', { tx: at.tx, tz: at.tz, on: held ? 0 : 1 }, { action });
                  setOpen(held ? null : action);
                }}
              >
                {held ? 'On' : 'Off'}
              </button>

              <button
                type="button"
                className="editor-way-name"
                disabled={!held}
                onClick={() => setOpen(showing ? null : action)}
              >
                {action}
                {held ? (
                  <small>
                    {held.catches.length > 0
                      ? `${held.catches.length} to catch`
                      : (items.find((one) => one.id === held.itemId)?.name ?? 'nothing listed')}
                  </small>
                ) : null}
              </button>

              {held ? (
                <span className="editor-way-fold" aria-hidden>
                  {showing ? '▾' : '▸'}
                </span>
              ) : null}
            </div>

            {held && showing ? (
              <ResourceEditor gathering={held} at={at} items={items} onCommand={onCommand} />
            ) : null}
          </div>
        );
      })}

      {/* An action nobody has thought of yet. Fishing has its four; anything
          else is whatever the world was built to call it. */}
      {!fishing && actions.length > offered.length ? (
        <p className="hint">Other menu entries here are not ways of gathering.</p>
      ) : null}
    </div>
  );
}

/**
 * What working at a piece of scenery gives.
 *
 * A tree is not interesting in itself: what matters is what comes off it, how
 * long it takes, and how good somebody has to be to get anything. Those live in
 * the skill file, which is a long way from the tree somebody is looking at, so
 * they are brought here instead.
 */
function ResourceEditor({
  gathering,
  at,
  items,
  onCommand,
}: {
  gathering: GatheringPacket;
  /** Which one is being worked on, since what it holds belongs to that one. */
  at: { tx: number; tz: number };
  items: readonly ItemDefinitionWire[];
  onCommand: AdminCommand;
}): JSX.Element {
  const change = (numbers: Record<string, number>): void =>
    onCommand(
      'setGathering',
      { object: gathering.objectId, ...numbers },
      { action: gathering.action },
    );

  /** Anything the numbers cannot say: which tools will do, and what is in there. */
  const write = (patch: Partial<GatheringPacket>): void => {
    const written = {
      objectId: gathering.objectId,
      action: gathering.action,
      skill: gathering.skill,
      level: gathering.level,
      experience: gathering.experience,
      itemId: gathering.itemId,
      interval: gathering.interval,
      chance: gathering.chance,
      animation: gathering.animation,
      tools: gathering.tools,
      catches: gathering.catches,
      ...patch,
    };

    // What comes up here belongs to the one standing here: two spots side by
    // side hold different fish. Everything else about the work, which tool and
    // how long it takes, is the same wherever it is done and stays with the
    // kind.
    if (patch.catches) {
      onCommand(
        'setSpotCatches',
        { tx: at.tx, tz: at.tz },
        { action: gathering.action, catches: JSON.stringify(patch.catches) },
      );
      return;
    }

    onCommand('setGathering', { object: gathering.objectId }, { json: JSON.stringify(written) });
  };

  const named = (itemId: number): string =>
    items.find((entry) => entry.id === itemId)?.name ?? `Item ${itemId}`;

  /** What the item itself says it takes and gives, for reading rather than setting. */
  const costOf = (itemId: number): string => {
    const item = items.find((entry) => entry.id === itemId);
    if (!item || item.gatherLevel <= 0) return 'no level set on the item';

    return `level ${item.gatherLevel}, ${Math.round(item.gatherExperience)} xp`;
  };

  /** What one weight comes to against the others, which is what somebody means. */
  const share = (weight: number): string => {
    const total = gathering.catches.reduce((sum, entry) => sum + entry.weight, 0);
    if (total <= 0) return 'all';

    return `${Math.round((weight / total) * 100)}%`;
  };

  return (
    <div className="editor-section">
      <h4>
        {gathering.action} &middot; {gathering.skill}
      </h4>

      {/* What has to be in the bag. A fishing spot is a spot for whatever tool
          it is written for: a net catches nothing a rod would. */}
      <label className="editor-line">
        <span>Tool</span>
        <ItemPicker
          items={items}
          value={gathering.tools[0] ?? 0}
          tag={`tool_${gathering.skill}`}
          allowNothing
          nothingLabel="nothing in hand"
          onPick={(tool) => write({ tools: tool > 0 ? [tool] : [] })}
        />
      </label>

      <label className="editor-line">
        <span>Animation</span>
        <select
          value={gathering.animation}
          onChange={(event) => write({ animation: event.target.value })}
        >
          <option value="none">nothing in particular</option>
          <option value="bend">bending over it</option>
        </select>
      </label>

      {/* What the walk to this particular one is worth, on top of whatever comes
          out of it. What a thing is worth belongs to the thing; this is about
          the place, which is why it is here and not on the item. */}
      <label className="editor-line">
        <span>Worth extra</span>
        <input
          type="number"
          min={0}
          max={1000}
          value={gathering.bonus}
          onChange={(event) =>
            onCommand(
              'setSpotBonus',
              { tx: at.tx, tz: at.tz, bonus: Math.max(0, Number(event.target.value)) },
              { action: gathering.action },
            )
          }
        />
        <small>Per cent, on this one only</small>
      </label>

      {/* Everything that can come up. Empty means the one thing below, which is
          what a tree or a rock is. */}
      <h4>Contents</h4>
      {gathering.catches.map((entry, index) => (
        <div className="editor-grid" key={`${entry.itemId}-${index}`}>
          <label className="editor-small">
            <span>Item</span>
            <ItemPicker
              items={items}
              value={entry.itemId}
              tag={`yield_${gathering.skill}`}
              onPick={(itemId) => {
                const catches = [...gathering.catches];
                catches[index] = { ...entry, itemId };
                write({ catches });
              }}
            />
          </label>

          {/* How often this one comes up against the others here, said as the
              share it actually works out to rather than as a bare weight. */}
          <label className="editor-small">
            <span>Weight</span>
            <input
              type="number"
              min={1}
              max={100}
              value={entry.weight}
              onChange={(event) => {
                const catches = [...gathering.catches];
                catches[index] = { ...entry, weight: Math.max(1, Number(event.target.value)) };
                write({ catches });
              }}
            />
            <small>{share(entry.weight)} of what is caught</small>
          </label>

          {/* What it takes and what it gives belong to the fish, not to the
              water: the same trout is the same trout wherever it turns up. Said
              here so nobody goes looking for the boxes that used to be here. */}
          <span className="editor-small quiet">{costOf(entry.itemId)}</span>

          <button
            type="button"
            className="editor-tab danger"
            aria-label={`Remove ${named(entry.itemId)}`}
            onClick={() => write({ catches: gathering.catches.filter((_, at) => at !== index) })}
          >
            Remove
          </button>
        </div>
      ))}

      <button
        type="button"
        className="wide"
        onClick={() =>
          write({
            catches: [
              ...gathering.catches,
              { itemId: gathering.itemId, weight: 1, level: 1, experience: 0 },
            ],
          })
        }
      >
        Add Catch
      </button>

      {gathering.catches.length === 0 ? (
        <label className="editor-line">
          <span>Yields</span>
          <ItemPicker
            items={items}
            value={gathering.itemId}
            tag={`yield_${gathering.skill}`}
            onPick={(itemId) => change({ item: itemId })}
          />
        </label>
      ) : (
        <p className="hint">The one thing it gives is only used while nothing is listed above.</p>
      )}

      <label className="editor-line">
        <span>Interval</span>
        <input
          type="number"
          min={1}
          max={20}
          value={gathering.interval}
          onChange={(event) => change({ interval: Number(event.target.value) })}
        />
        <small>ticks</small>
      </label>

      <label className="editor-line">
        <span>Chance</span>
        <input
          type="number"
          min={1}
          max={100}
          value={Math.round(gathering.chance * 100)}
          onChange={(event) => change({ chance: Number(event.target.value) / 100 })}
        />
        <small>%</small>
      </label>

      <button type="button" className="wide" onClick={() => onCommand('saveSkills', {})}>
        Save skills
      </button>
    </div>
  );
}

function CharacterDetails({
  entityId,
  at,
  facts,
  onTalk,
  drops,
  dropTest,
  shop,
  items,
  quests,
  characters,
  shapes,
  lastArea,
  painting,
  drawingRoam,
  onDrawRoam,
  onPainting,
  onTool,
  onCommand,
  onClear,
}: {
  entityId: number;
  /** Where it is stationed, when it was chosen from a list rather than clicked. */
  at: { tx: number; tz: number } | null;
  facts: Facts | null;
  drops: DropTablePacket | null;
  dropTest: DropTest | null;
  shop: ShopStockPacket | null;
  /** Every shop there is, for a line of conversation that opens one. */
  /** Opens this character's conversation on the canvas. */
  onTalk: (npcId: number) => void;
  items: readonly ItemDefinitionWire[];
  /** For a drop roll that only turns partway through a quest. */
  quests: readonly QuestWire[];
  characters: readonly NpcDefinitionWire[];
  /** Everything there is, for a condition that names a piece of scenery. */
  shapes: readonly { id: number; name: string }[];
  lastArea: TileArea | null;
  /**
   * Whether the next box dragged on the ground is this one's roam area.
   *
   * Armed from here rather than read off whatever was drawn last: dragging a box
   * used to change what was held, so by the time somebody pressed the button the
   * character they drew it for was no longer the character being edited.
   */
  drawingRoam: boolean;
  onDrawRoam: (on: boolean) => void;
  /** Whether painting adds ground or takes it away, shared with the zones. */
  painting: 'add' | 'remove';
  onPainting: (mode: 'add' | 'remove') => void;
  onTool: (tool: TransformTool) => void;
  onCommand: AdminCommand;
  onClear: () => void;
}): JSX.Element {
  // Where it stands, what it sells and what it drops are three different jobs.
  // They get a tab each rather than one column somebody has to scroll through.
  const [section, setSection] = useState<'spawn' | 'says' | 'talk' | 'shop' | 'drops'>('spawn');

  // Matched on whichever it was asked about: the thing that was clicked, or the
  // tile a list said it was stationed on.
  const known =
    facts && facts.kind === 'spawn'
      ? entityId !== 0
        ? facts.numbers.entity === entityId
          ? facts
          : null
        : at && facts.numbers.tx === at.tx && facts.numbers.tz === at.tz
          ? facts
          : null
      : null;

  if (!known) return <p className="hint">Asking the server about this one.</p>;

  const entity = known.numbers.entity ?? entityId;
  const npcId = known.numbers.npc ?? 0;
  const respawn = known.numbers.respawn ?? 0;
  const roamWidth = known.numbers.roamWidth;
  const roamHeight = known.numbers.roamHeight;
  const roamTiles = known.numbers.roamTiles ?? 0;
  const fromZone = (known.numbers.zone ?? 0) > 0;

  return (
    <>
      <h3>{known.text.name ?? 'Character'}</h3>

      <div className="editor-tabs">
        {(['spawn', 'says', 'talk', 'shop', 'drops'] as const).map((entry) => (
          <button
            key={entry}
            type="button"
            className={section === entry ? 'editor-tab on' : 'editor-tab'}
            onClick={() => setSection(entry)}
          >
            {entry === 'spawn'
              ? 'Placement'
              : entry === 'says'
                ? 'Identity'
                : entry === 'talk'
                  ? 'Dialogue'
                  : entry === 'shop'
                    ? 'Shop'
                    : 'Drops'}
          </button>
        ))}
      </div>

      {section === 'spawn' ? (
        <>
          <div className="editor-fact">
            <span>Spawn</span>
            <span>
              {known.numbers.tx}, {known.numbers.tz}
            </span>
          </div>
          <div className="editor-fact">
            <span>Level</span>
            <span>{known.numbers.combatLevel ?? 0}</span>
          </div>

          {fromZone ? (
            <p className="hint">
              One of a zone, not a spawn of its own.
              <Explain>
                What it is and how often it comes back belong to the zone, which has a tab of its
                own when this tile is clicked.
              </Explain>
            </p>
          ) : (
            <>
              <div className="editor-fact">
                <span>Respawn</span>
                <span>{secondsOf(respawn)}s</span>
              </div>
              <div className="editor-fact">
                <span>Roam</span>
                <span>
                  {roamTiles > 0
                    ? `${roamTiles} tiles, drawn`
                    : roamWidth && roamHeight
                      ? `${roamWidth} by ${roamHeight}`
                      : `${known.numbers.wander ?? 0} tiles`}
                </span>
              </div>

              <Slider
                label="Respawn (ticks)"
                value={respawn}
                min={1}
                max={500}
                onChange={(value) => onCommand('setSpawn', { entity, respawn: value })}
              />

              <h4>Where It May Wander</h4>

              <div className="editor-brushes">
                <button
                  type="button"
                  className="editor-tab"
                  title="It stays on the tile it is standing on"
                  onClick={() => onCommand('setSpawn', { entity }, { roam: 'here' })}
                >
                  Stay Put
                </button>

                <button
                  type="button"
                  className={drawingRoam ? 'editor-tab on' : 'editor-tab'}
                  title="Then drag a box on the ground"
                  onClick={() => onDrawRoam(!drawingRoam)}
                >
                  {drawingRoam ? 'Drag a Box...' : 'Draw a Box'}
                </button>

                <button
                  type="button"
                  className="editor-tab"
                  title="It wanders wherever it likes"
                  onClick={() => onCommand('setSpawn', { entity }, { roam: 'clear' })}
                >
                  Anywhere
                </button>
              </div>

              <p className="hint">
                {drawingRoam
                  ? 'Drag a box on the ground. Whatever it covers is where this one may wander.'
                  : 'Stay Put keeps it on its tile. A box holds it to that ground.'}
                <Explain>
                  Drawing a box while this is lit changes only where this character may wander: what
                  is held in the editor is left alone, which is what went wrong before.
                </Explain>
              </p>

              {lastArea ? (
                <button
                  type="button"
                  className="wide"
                  title={`${lastArea.width} by ${lastArea.height} at ${lastArea.tx}, ${lastArea.tz}`}
                  onClick={() =>
                    onCommand('setSpawn', {
                      entity,
                      roamTx: lastArea.tx,
                      roamTz: lastArea.tz,
                      roamWidth: lastArea.width,
                      roamHeight: lastArea.height,
                    })
                  }
                >
                  Use The Box Already Drawn
                </button>
              ) : null}

              <Spots
                entity={entity}
                spots={spotsOf(known.text.spots ?? '[]')}
                at={{ tx: known.numbers.tx ?? 0, tz: known.numbers.tz ?? 0 }}
                lastArea={lastArea}
                items={items}
                quests={quests}
                characters={characters}
                objects={shapes}
                onCommand={onCommand}
              />

              {/* Painting it rather than dragging a box round it, for a
                  riverbank or a clearing, which are not rectangles. */}
              <h4>Paint It Instead</h4>
              <div className="editor-brushes">
                <button
                  type="button"
                  className={painting === 'add' ? 'editor-tab on' : 'editor-tab'}
                  onClick={() => onPainting('add')}
                >
                  Add
                </button>
                <button
                  type="button"
                  className={painting === 'remove' ? 'editor-tab on' : 'editor-tab'}
                  onClick={() => onPainting('remove')}
                >
                  Remove
                </button>
                <button type="button" className="editor-tab" onClick={() => onTool('brush')}>
                  Brush
                </button>
              </div>
            </>
          )}

          <button
            type="button"
            className="wide danger"
            onClick={() => {
              onCommand('removeSpawn', { entity });
              onClear();
            }}
          >
            Remove this character
          </button>
        </>
      ) : null}

      {section === 'talk' ? (
        <>
          {/* The conversation itself is written on the canvas, which is a window
              of its own: a graph does not fit in a column three hundred pixels
              wide, and what somebody says belongs to the kind of person rather
              than to this one standing on this tile. */}
          <p className="hint">{known.numbers.dialogue ?? 0} dialogue nodes.</p>
          <button type="button" className="wide primary" onClick={() => onTalk(npcId)}>
            Open Dialogue Editor
          </button>

          {/* What they say to nobody in particular. Kept here rather than beside
              the name, where it read as a second and worse conversation, and
              only reachable at all because a few characters still have one. */}
          <h4>Chatter</h4>
          <LinesEditor
            npcId={npcId}
            lines={known.text.lines ?? ''}
            conversation={known.numbers.dialogue ?? 0}
            onCommand={onCommand}
          />
        </>
      ) : null}

      {section === 'says' ? (
        <>
          <h4>Identity</h4>
          <SettledField
            label="Name"
            value={known.text.name ?? ''}
            limit={48}
            onCommit={(name) => onCommand('setNpc', { npc: npcId }, { name })}
          />
          <SettledField
            label="Description"
            value={known.text.examine ?? ''}
            placeholder="What looking at them says"
            limit={160}
            onCommit={(examine) => onCommand('setNpc', { npc: npcId }, { examine })}
          />
          <ModelChoice
            held={known.text.model ?? 'placeholder'}
            onChange={(model) => onCommand('setNpc', { npc: npcId }, { model })}
          />

          <h4>Colours</h4>
          <ColourChoice
            skin={colourText(known.numbers.skin ?? 0)}
            eyes={colourText(known.numbers.eyes ?? 0)}
            onChange={(which, colour) =>
              onCommand('setNpc', { npc: npcId, [which]: colourValue(colour) })
            }
          />
          <p className="hint">
            For a painted model, not a dressed one.
            <Explain>Black on both leaves the model the colours it was made with.</Explain>
          </p>

          <p className="hint">
            Changes every one of this kind in the world.
            <Explain>Not only the one that is selected.</Explain>
          </p>

          <button type="button" className="wide" onClick={() => onCommand('saveCharacters')}>
            Save the characters
          </button>
        </>
      ) : null}

      {section === 'shop' ? (
        <ShopEditor
          npcId={npcId}
          shop={shop}
          items={items}
          quests={quests}
          characters={characters}
          objects={shapes}
          onCommand={onCommand}
        />
      ) : null}

      {section === 'drops' ? (
        <DropEditor
          npcId={npcId}
          drops={drops}
          test={dropTest}
          items={items}
          quests={quests}
          characters={characters}
          objects={shapes}
          onCommand={onCommand}
        />
      ) : null}
    </>
  );
}

/**
 * What a kind of character is drawn with.
 *
 * One model, and any number of others it may also be drawn as. A spawn takes one
 * of them at random, which is how a camp of goblins reads as a camp rather than a
 * row of the same goblin. The two are asked separately because picking a model
 * and adding a variant are different intentions, and a picker that treated them
 * as the same turned every choice into a second variant.
 */
function ModelChoice({
  held,
  onChange,
}: {
  held: string;
  onChange: (model: string) => void;
}): JSX.Element {
  const chosen = held
    .split(',')
    .map((name) => name.trim())
    .filter((name) => name.length > 0);

  const main = chosen[0] ?? 'placeholder';
  const variants = chosen.slice(1);

  const pick = (name: string): void => {
    onChange([name, ...variants.filter((entry) => entry !== name)].join(','));
  };

  const toggleVariant = (name: string): void => {
    const next = variants.includes(name)
      ? variants.filter((entry) => entry !== name)
      : [...variants, name];

    onChange([main, ...next.filter((entry) => entry !== main)].join(','));
  };

  return (
    <>
      <h4>Model</h4>
      <div className="editor-brushes">
        {MODEL_NAMES.map((name) => (
          <button
            key={name}
            type="button"
            className={main === name ? 'editor-tab on' : 'editor-tab'}
            onClick={() => pick(name)}
          >
            {name}
          </button>
        ))}
      </div>

      <h4>Attachments</h4>
      <div className="editor-brushes">
        {MODEL_NAMES.filter((name) => name !== main).map((name) => (
          <button
            key={name}
            type="button"
            className={variants.includes(name) ? 'editor-tab on' : 'editor-tab'}
            onClick={() => toggleVariant(name)}
          >
            {name}
          </button>
        ))}
      </div>

      <p className="hint">
        {variants.length > 0
          ? `${variants.length + 1} variants: each of these spawns is drawn as one of them.`
          : 'Every one of these is drawn the same way. Add a variant and they stop being.'}
      </p>
    </>
  );
}

/**
 * A spawn zone: what comes back in it, how many, how often, and where it reaches.
 *
 * The shape is drawn rather than typed. The brush adds and takes away a square at
 * a time, and a dragged area does the same over a rectangle, which is enough to
 * follow a clearing or leave a path through one.
 */
function ZoneDetails({
  zone,
  characters,
  brush,
  painting,
  onPainting,
  onTool,
  onCommand,
  onClear,
}: {
  zone: ZoneWire | undefined;
  characters: readonly NpcDefinitionWire[];
  brush: number;
  painting: 'add' | 'remove';
  onPainting: (mode: 'add' | 'remove') => void;
  onTool: (tool: TransformTool) => void;
  onCommand: AdminCommand;
  onClear: () => void;
}): JSX.Element {
  if (!zone) return <p className="hint">That zone is gone.</p>;

  const fighters = characters.filter((entry) => entry.combatLevel > 0);
  const npc = characters.find((entry) => entry.id === zone.npcId);

  return (
    <>
      <h3>{zone.name || (npc?.name ?? 'Zone')}</h3>

      <label className="editor-field">
        <span>Name</span>
        <input
          type="text"
          value={zone.name}
          placeholder={npc?.name ?? 'Zone'}
          onChange={(event) =>
            onCommand('setZone', { zone: zone.id }, { name: event.target.value.slice(0, 32) })
          }
        />
      </label>
      <div className="editor-fact">
        <span>Population</span>
        <span>
          {zone.alive} of {zone.max}
        </span>
      </div>
      <div className="editor-fact">
        <span>Tiles</span>
        <span>{zone.tiles.length}</span>
      </div>

      {zone.paused ? (
        <p className="hint">Paused: it keeps its shape and its numbers but puts nothing out.</p>
      ) : null}

      {zone.alive === 0 && !zone.paused ? (
        <p className="hint">
          Nothing has come back yet.
          <Explain>
            If it stays empty, the ground it covers may be water or scenery rather than somewhere
            anything can stand.
          </Explain>
        </p>
      ) : null}

      <label className="editor-field">
        <span>Character</span>
        <select
          value={zone.npcId}
          onChange={(event) =>
            onCommand('setZone', { zone: zone.id, npc: Number(event.target.value) })
          }
        >
          {fighters.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.name} (level {entry.combatLevel})
            </option>
          ))}
        </select>
      </label>

      <Slider
        label="Population"
        value={zone.max}
        min={1}
        max={24}
        onChange={(value) => onCommand('setZone', { zone: zone.id, max: value })}
      />
      <Slider
        label="Min Respawn"
        value={zone.minTicks}
        min={1}
        max={600}
        suffix=" ticks"
        onChange={(value) =>
          onCommand('setZone', {
            zone: zone.id,
            minTicks: value,
            maxTicks: Math.max(value, zone.maxTicks),
          })
        }
      />
      <Slider
        label="Max Respawn"
        value={zone.maxTicks}
        min={1}
        max={600}
        suffix=" ticks"
        onChange={(value) =>
          onCommand('setZone', {
            zone: zone.id,
            maxTicks: value,
            minTicks: Math.min(value, zone.minTicks),
          })
        }
      />
      <p className="hint">
        One comes back every {secondsOf(zone.minTicks)} to {secondsOf(zone.maxTicks)} seconds, up to{' '}
        {zone.max} at a time.
      </p>

      <h3>Shape</h3>
      <div className="editor-brushes">
        <button
          type="button"
          className={painting === 'add' ? 'editor-tab on' : 'editor-tab'}
          onClick={() => {
            onPainting('add');
            onTool('select');
          }}
        >
          Add
        </button>
        <button
          type="button"
          className={painting === 'remove' ? 'editor-tab on' : 'editor-tab'}
          onClick={() => {
            onPainting('remove');
            onTool('select');
          }}
        >
          Remove
        </button>
      </div>
      <p className="hint">
        With the zone selected, click to {painting === 'add' ? 'add' : 'remove'} a {brush} by{' '}
        {brush} square, or drag out an area to do the same over all of it.
      </p>
      <h3>Brush</h3>
      <div className="editor-brushes">
        <button
          type="button"
          className="editor-tab"
          onClick={() => onCommand('fillZone', { zone: zone.id })}
        >
          Fill it now
        </button>
        <button
          type="button"
          className={zone.paused ? 'editor-tab on' : 'editor-tab'}
          onClick={() =>
            onCommand('setZone', { zone: zone.id }, { paused: zone.paused ? 'no' : 'yes' })
          }
        >
          {zone.paused ? 'Paused' : 'Pause it'}
        </button>
      </div>

      {/* What the world itself has to be thinking before it puts anything out.
          Against the world rather than against a player, because what stands in
          a field is the same for everybody looking at it. */}
      <label className="editor-field">
        <span>
          Waits for
          <Explain>
            A flag the world holds, not one a player holds. Leave it empty and it spawns as it
            always did. This is how something appears once a beacon is lit rather than once one
            person has lit it.
          </Explain>
        </span>
        <input
          type="text"
          value={zone.needsWorld}
          placeholder="nothing"
          onChange={(event) =>
            onCommand('setZone', { zone: zone.id }, { needsWorld: event.target.value })
          }
        />
      </label>

      <label className="editor-field">
        <span>
          Only at
          <Explain>
            The stretch of day it puts anything out in. Any time at all unless it says otherwise,
            which is what nearly every zone wants.
          </Explain>
        </span>
        <select
          value={zone.atTime}
          onChange={(event) =>
            onCommand('setZone', { zone: zone.id }, { atTime: event.target.value })
          }
        >
          <option value="">Any time</option>
          <option value="dawn">Dawn</option>
          <option value="day">Daytime</option>
          <option value="dusk">Dusk</option>
          <option value="night">Night</option>
        </select>
      </label>

      {zone.needsWorld.length > 0 ? (
        <label className="editor-field">
          <span>Saying</span>
          <input
            type="text"
            value={zone.worldIs}
            placeholder="anything"
            onChange={(event) =>
              onCommand('setZone', { zone: zone.id }, { worldIs: event.target.value })
            }
          />
        </label>
      ) : null}

      <div className="editor-brushes">
        <button
          type="button"
          className="editor-tab"
          onClick={() => onCommand('growZone', { zone: zone.id, by: 1 })}
        >
          Grow a ring
        </button>
        <button
          type="button"
          className="editor-tab"
          onClick={() => onCommand('growZone', { zone: zone.id, by: -1 })}
        >
          Pull a ring in
        </button>
        <button
          type="button"
          className="editor-tab"
          onClick={() => onCommand('emptyZone', { zone: zone.id })}
        >
          Take them out
        </button>
      </div>

      <button
        type="button"
        className="wide"
        onClick={() => onCommand('trimZone', { zone: zone.id })}
      >
        Drop tiles nothing can stand on
      </button>

      <button
        type="button"
        className="wide danger"
        onClick={() => {
          onCommand('removeZone', { zone: zone.id });
          onClear();
        }}
      >
        Remove the zone
      </button>
    </>
  );
}

/**
 * What a character says when talked to.
 *
 * A list of lines, said one at a time in order, which is the whole of
 * conversation for now: enough to point somebody at the anvil or warn them off
 * the marsh. Branching will hang off this rather than replace it.
 */
function LinesEditor({
  npcId,
  lines,
  conversation,
  onCommand,
}: {
  npcId: number;
  /** The lines as the server keeps them, joined with bars. */
  lines: string;
  /** How many nodes their conversation holds, which takes over from these. */
  conversation: number;
  onCommand: AdminCommand;
}): JSX.Element {
  const [draft, setDraft] = useState('');
  const held = lines.length > 0 ? lines.split('|') : [];

  // A conversation is the whole of what they say once one is written, so the
  // list is left as a record of what was there rather than as a second way of
  // saying it. Two answers to one question is how a character ends up saying
  // something nobody can find in the editor.
  const talking = conversation > 0;

  const send = (next: string[]): void => {
    onCommand('setLines', { npc: npcId }, { lines: next.join('|') });
  };

  return (
    <>
      {held.map((line, index) => (
        <div className="editor-drop" key={`${index}-${line}`}>
          <span className="editor-drop-name">{line}</span>
          <span className="editor-drop-times">{index + 1}</span>
          <button
            type="button"
            className="editor-drop-remove"
            title="Delete this line"
            onClick={() => send(held.filter((_, at) => at !== index))}
          >
            ✕
          </button>
        </div>
      ))}

      {held.length === 0 && !talking ? <p className="hint">It says nothing at all.</p> : null}

      {talking ? (
        <p className="hint">
          Their conversation says it instead.
          <Explain>
            A written conversation takes over from this list entirely, so the lines here are never
            reached. Open Talk to write it.
          </Explain>
        </p>
      ) : null}

      <div className="editor-drop-add">
        <input
          className="editor-search"
          type="text"
          value={draft}
          disabled={talking}
          placeholder={talking ? 'Their conversation says it' : 'Something for it to say'}
          onChange={(event) => setDraft(event.target.value)}
        />
        <button
          type="button"
          className="wide"
          disabled={talking || draft.trim().length === 0}
          title={talking ? 'Their conversation says it instead' : undefined}
          onClick={() => {
            send([...held, draft.trim()]);
            setDraft('');
          }}
        >
          Have it say that
        </button>

        <button type="button" className="wide" onClick={() => onCommand('saveCharacters')}>
          Save the characters
        </button>
      </div>
    </>
  );
}

/** What the ground is made of, in the words the tools use for it. */
function groundName(kind: number): string {
  return ['natural', 'grass', 'dirt', 'sand', 'rock', 'path'][kind] ?? 'natural';
}

/** How many of each kind of thing is held, most first. */
function countByKind(
  held: readonly { objectId: number }[],
  describeObject: (id: number) => { name: string } | undefined,
): Map<string, number> {
  const counted = new Map<string, number>();

  for (const pick of held) {
    const what = describeObject(pick.objectId)?.name ?? `Scenery ${pick.objectId}`;
    counted.set(what, (counted.get(what) ?? 0) + 1);
  }

  return new Map([...counted].sort((a, b) => b[1] - a[1]));
}

/**
 * One of the other places a character stands, as the tools hold it.
 *
 * The tiles are the world's own, the way every other tile in the panel is: the
 * server turns them into region tiles when it writes them down, so nothing in
 * the interface has to know where a region begins.
 */
export interface SpotWire {
  name: string;
  tx: number;
  tz: number;
  when: Condition[];
  walk: boolean;
  roam?: { tx: number; tz: number; width: number; height: number };
}

/** The spots as the world sent them, or none where it will not read. */
function spotsOf(written: string): SpotWire[] {
  try {
    const parsed: unknown = JSON.parse(written);
    return Array.isArray(parsed) ? (parsed as SpotWire[]) : [];
  } catch {
    return [];
  }
}

/**
 * Everywhere else one character stands, and what has to be true for each.
 *
 * The tile it was placed on is where it stands when nothing applies, so the
 * list here is the exceptions: on the beach until somebody has spoken to it, up
 * by the house afterwards. The first spot whose conditions a player meets is
 * where that player sees it, so the order is the order they are read in.
 */
function Spots({
  entity,
  spots,
  at,
  lastArea,
  items,
  quests,
  characters,
  objects,
  onCommand,
}: {
  entity: number;
  spots: readonly SpotWire[];
  /** Where the character is stationed, for a spot that starts from there. */
  at: { tx: number; tz: number };
  /** The last box drawn on the ground, which a spot can take as its roaming. */
  lastArea: TileArea | null;
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  characters: readonly NpcDefinitionWire[];
  objects: readonly { id: number; name: string }[];
  onCommand: AdminCommand;
}): JSX.Element {
  const [open, setOpen] = useState<number | null>(null);

  const write = (next: SpotWire[]): void => {
    onCommand('setSpots', { entity }, { spots: JSON.stringify(next) });
  };

  const change = (index: number, patch: Partial<SpotWire>): void => {
    write(spots.map((one, held) => (held === index ? { ...one, ...patch } : one)));
  };

  return (
    <>
      <h4>Other Spots</h4>

      {spots.length === 0 ? (
        <p className="hint">
          It stands where it was placed, for everybody.
          <Explain>
            A spot is somewhere else it stands, for whoever meets the conditions on it. The one it
            was placed on is what everybody else sees.
          </Explain>
        </p>
      ) : null}

      {spots.map((spot, index) => (
        <div className="editor-spot" key={`${index}-${spot.tx}-${spot.tz}`}>
          <div className="editor-spot-head">
            <button
              type="button"
              className="editor-spot-name"
              title="What has to be true for it to stand here"
              onClick={() => setOpen(open === index ? null : index)}
            >
              {spot.name || `Spot ${index + 1}`}{' '}
              <span className="quiet">{open === index ? '▾' : '▸'}</span>
            </button>

            <span className="quiet">
              {spot.tx}, {spot.tz}
            </span>

            <button
              type="button"
              title="Take this spot away"
              onClick={() => write(spots.filter((_, held) => held !== index))}
            >
              ✕
            </button>
          </div>

          {open === index ? (
            <>
              <SettledField
                label="Called"
                value={spot.name}
                limit={32}
                onCommit={(name) => change(index, { name })}
              />

              <div className="editor-brushes">
                <button
                  type="button"
                  className={spot.walk ? 'editor-tab on' : 'editor-tab'}
                  title="It walks there while anybody watching can see it"
                  onClick={() => change(index, { walk: !spot.walk })}
                >
                  Walk There
                </button>

                <button
                  type="button"
                  className="editor-tab"
                  title="Move this spot to where the character is standing now"
                  onClick={() => change(index, { tx: at.tx, tz: at.tz })}
                >
                  Use Its Tile
                </button>

                <button
                  type="button"
                  className="editor-tab"
                  disabled={!lastArea}
                  title="Hold it to the box last drawn on the ground"
                  onClick={() => {
                    if (!lastArea) return;
                    change(index, {
                      roam: {
                        tx: lastArea.tx,
                        tz: lastArea.tz,
                        width: lastArea.width,
                        height: lastArea.height,
                      },
                    });
                  }}
                >
                  Roam In The Box
                </button>

                {spot.roam ? (
                  <button
                    type="button"
                    className="editor-tab"
                    title="It stays on this tile"
                    onClick={() => change(index, { roam: undefined })}
                  >
                    Stay Put Here
                  </button>
                ) : null}
              </div>

              <p className="hint">
                {spot.walk
                  ? 'It walks there when the conditions are met.'
                  : 'It is simply there, behind a blink of black for anybody watching.'}
                {spot.roam
                  ? ` It wanders ${spot.roam.width} by ${spot.roam.height} from there.`
                  : ' It stays on the tile.'}
              </p>

              <Conditions
                held={spot.when}
                items={items}
                quests={quests}
                characters={characters}
                objects={objects}
                onChange={(when) => change(index, { when })}
              />
            </>
          ) : null}
        </div>
      ))}

      <button
        type="button"
        className="wide"
        onClick={() => write([...spots, { name: '', tx: at.tx, tz: at.tz, when: [], walk: false }])}
      >
        Add A Spot Here
      </button>
    </>
  );
}

/**
 * Which named place a stretch of ground belongs to, and what it is called.
 *
 * The one line in the world that is drawn per tile rather than as a rectangle,
 * so a border can follow a river or the foot of a range. That is also what made
 * it the only line nobody could move: it was written when the continent was
 * built and there was nothing in the tools that touched it, so adjusting where
 * the Meridian ends meant regenerating the continent and losing everything
 * anybody had built on it.
 *
 * Painting works from whatever is held: the last area dragged out if there is
 * one, otherwise the brush under the pointer.
 */
function PlaceDetails({
  areas,
  here,
  lastArea,
  onCommand,
}: {
  areas: readonly { name: string; town?: boolean }[];
  here: number;
  lastArea: TileArea | null;
  onCommand: AdminCommand;
}): JSX.Element {
  const [chosen, setChosen] = useState(here);
  const [naming, setNaming] = useState(false);
  const [called, setCalled] = useState('');

  const holding = lastArea !== null;
  const which = chosen > 0 && chosen <= areas.length ? chosen : here;
  const name = (at: number): string =>
    at === 0 ? 'Nowhere in particular' : (areas[at - 1]?.name ?? `Place ${at}`);

  return (
    <>
      <h4>Place</h4>
      <div className="editor-fact">
        <span>Here</span>
        <span>{name(here)}</span>
      </div>

      <select
        className="editor-select"
        value={which}
        onChange={(event) => setChosen(Number(event.target.value))}
      >
        <option value={0}>Nowhere in particular</option>
        {areas.map((area, at) => (
          <option key={`${area.name}-${at}`} value={at + 1}>
            {area.name}
            {area.town ? ' (town)' : ''}
          </option>
        ))}
      </select>

      <div className="editor-row">
        <button
          type="button"
          disabled={!holding}
          onClick={() => {
            if (!lastArea) return;
            onCommand('paintPlace', {
              tx: lastArea.tx,
              tz: lastArea.tz,
              width: lastArea.width,
              height: lastArea.height,
              area: which,
            });
          }}
        >
          {holding ? 'Put the held ground here' : 'Drag out ground first'}
        </button>
        <button type="button" onClick={() => setNaming((open) => !open)}>
          {naming ? 'Done' : 'Name it'}
        </button>
      </div>

      {naming ? (
        <div className="editor-row">
          <input
            className="editor-input"
            value={called}
            placeholder={which > 0 ? name(which) : 'A name for a new place'}
            maxLength={48}
            onChange={(event) => setCalled(event.target.value)}
          />
          <button
            type="button"
            disabled={called.trim().length === 0}
            onClick={() => {
              onCommand('namePlace', { area: which }, { name: called.trim() });
              setCalled('');
              setNaming(false);
            }}
          >
            {which > 0 ? 'Rename' : 'Add'}
          </button>
        </div>
      ) : null}

      <p className="hint">
        A border is drawn tile by tile, so it can follow a river. Choosing nothing and painting
        takes ground out of every place.
      </p>
    </>
  );
}
